(() => {
  if (customElements.get('lc-davines-pump')) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const cards = () => [...document.querySelectorAll('lc-davines-pump')];
  const inCart = (cart, id) => cart.items.some(item => String(item.variant_id || item.id) === String(id));
  const dismissed = new Set();
  let cartRequest;
  let pending = false;
  let refreshTimer;
  let lastCart;

  function isDismissed(family) {
    try { return dismissed.has(family) || sessionStorage.getItem('lc-pump-dismiss-' + family) === '1'; }
    catch { return dismissed.has(family); }
  }

  async function request(path, payload) {
    const response = await fetch(root() + path, {
      method: payload ? 'POST' : 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', ...(payload ? {'Content-Type': 'application/json'} : {}) },
      ...(payload ? { body: JSON.stringify(payload) } : {}),
      signal: AbortSignal.timeout(12000)
    });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data.description || data.message || 'Cart request failed');
      error.confirmedFailure = true;
      throw error;
    }
    return data;
  }

  function paint(cart) {
    if (cart?.items) lastCart = cart;
    cards().forEach(card => card.paint(lastCart));
  }

  function readCart() {
    if (!cartRequest) {
      cartRequest = request('cart.js').then(cart => { paint(cart); return cart; }).finally(() => { cartRequest = null; });
    }
    return cartRequest;
  }

  function refresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => { if (cards().length) readCart().catch(() => {}); }, 150);
  }

  class LCPump extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-pump-add]');
      this.label = this.querySelector('[data-pump-label]');
      this.originalLabel = this.label?.textContent;
      this.status = this.querySelector('[data-pump-status]');
      this.dismiss = this.querySelector('[data-pump-dismiss]');
      if (this.dismiss) this.dismiss.hidden = false;
      this.paint(lastCart);
      if (this.dataset.mode === 'product') refresh();
    }

    paint(cart) {
      const present = cart ? inCart(cart, this.dataset.pumpId) : Number(this.dataset.pumpCount) > 0;
      if (this.dataset.mode === 'cart') this.hidden = present || isDismissed(this.dataset.family);
      if (this.button) {
        this.button.disabled = pending || present || this.uncertain === true;
        this.label.textContent = present ? this.dataset.added : this.busy ? this.dataset.adding : this.originalLabel;
      }
    }

    async add() {
      if (pending || !this.button || this.button.disabled) return;
      pending = true;
      this.busy = true;
      this.status.textContent = '';
      paint(lastCart);
      let attempted = false;
      let confirmedFailure = false;
      try {
        const before = await readCart();
        if (inCart(before, this.dataset.pumpId)) return;
        if (this.dataset.mode === 'cart' && !inCart(before, this.dataset.bottleId)) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }
        attempted = true;
        try { await request('cart/add.js', { items: [{ id: Number(this.dataset.pumpId), quantity: 1 }] }); }
        catch (error) { confirmedFailure = error.confirmedFailure === true; throw error; }
        const cart = await readCart();
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'lc-pump:add', cart }
        }));
        if (location.pathname.replace(/\/$/, '').endsWith('/cart')) location.reload();
      } catch (error) {
        let recovered = false;
        if (attempted) {
          try {
            const cart = await readCart();
            if (inCart(cart, this.dataset.pumpId)) {
              recovered = true;
              document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'lc-pump:add', cart } }));
              if (location.pathname.replace(/\/$/, '').endsWith('/cart')) location.reload();
            }
          } catch {}
        }
        if (!recovered) {
          this.uncertain = attempted && !confirmedFailure;
          this.status.textContent = this.uncertain ? this.dataset.review : this.dataset.error;
        }
      } finally {
        pending = false;
        this.busy = false;
        paint(lastCart);
      }
    }
  }

  customElements.define('lc-davines-pump', LCPump);
  // Delegate actions so controls survive the theme's cached variant block swaps.
  document.addEventListener('click', event => {
    const action = event.target.closest('[data-pump-add], [data-pump-dismiss]');
    const card = action?.closest('lc-davines-pump');
    if (!card) return;
    event.preventDefault();
    if (action.hasAttribute('data-pump-dismiss')) {
      dismissed.add(card.dataset.family);
      try { sessionStorage.setItem('lc-pump-dismiss-' + card.dataset.family, '1'); } catch {}
      paint(lastCart);
    } else {
      card.add();
    }
  });
  document.addEventListener('cart:change', event => { if (event.detail?.cart?.items) paint(event.detail.cart); else refresh(); });
  document.addEventListener('cart:refresh', refresh);
  document.addEventListener('cart-drawer:refreshed', refresh);
  window.addEventListener('pageshow', refresh);
})();
